<template>
  <Modal v-model:open="modalState" :trigger="trigger" :title="title" :description="description" footer-alignment="right" :with-expand-button="withExpandButton" :with-close-button="withCloseButton" :full-height="fullHeight" :small="small" :footer-visible="footerVisible" @update:open="handleModalClose">
    <div class="w-full h-auto px-2 py-2 modalform" >
      <div class="grid gap-x-10 gap-y-6 grid-cols-1" :class="{'sm:grid-cols-2':multipleColumns}">
        <FormGroup v-for="(item, index) in visibleFields" :key="index" :label="item.noTranslate? item.label : $t(item.label)" :required="item.required" :html-for="item.id" :tooltip="item.tooltip" no-margin>

          <CGInput v-if="item.type == 'text' || item.type == 'number'" :id="item.id" v-model="dataItem[item.id]" :type="item.type" :readonly="item.readonly" :disabled="item.disabled" :placeholder="$t(item.placeholder || item.label)"
            :errored="item.errored" :error-message="item.error" :min="item.min" :max="item.max" :step="item.step" @update:modelValue="validate($event, item);propagateEvent($event, item)" />
          <CGSelect v-if="item.type == 'select'" :id="item.id" v-model="dataItem[item.id]" :placeholder="$t(item.placeholder || item.label)" :multiple="item.multiple" :readonly="item.readonly" :disabled="item.disabled"
            :modelValue="dataItem[item.id]" :options="item.options" :erasable="item.erasable" @update:modelValue="validate($event, item);propagateEvent($event, item)"/>
          <Toggle v-if="item.type == 'toggle'" :id="item.id" v-model="dataItem[item.id]" :checked-value="item.checkedValue" :unchecked-value="item.uncheckedValue"
            :modelValue="dataItem[item.id]" :disabled="item.disabled" @update:modelValue="propagateEvent($event, item)"/>
          <DatePicker v-if="item.type == 'date'" :id="item.id" v-model="dataItem[item.id]" :disabled="item.disabled"
            :errored="item.errored" :error-message="item.error" :nullable="item.nullable" @update:modelValue="validate($event, item)"/>
          <InputTags v-if="item.type == 'tag-input'" :id="item.id" v-model="dataItem[item.id]" :options="item.options" :allow-others="item.allowOthers" :max="item.max"
            :placeholder="$t(item.placeholder || item.label)" @update:modelValue="manageTag(item, $event)" @remove="manageTag(item, $event)" />
          <CGTextarea
            v-if="item.type == 'textarea'" :id="item.id" v-model="dataItem[item.id]" :rows="item.rows" :cols="item.cols" :readonly="item.readonly" :disabled="item.disabled"
            :placeholder="$t(item.placeholder || item.label)" :errored="item.errored" :error-message="item.error" :no-resize="item.noResize"
            @update:modelValue="validate($event, item)"
           />
          <img v-if="item.type == 'image'" :src="dataItem[item.id]" />
          <Txt v-if="item.info" extraSmall class="text-violet-700 mt-1 flex items-center"><Icon name="info" /> {{item.info}}</Txt>

          <!-- File input field -->
          <CGInput v-if="item.type == 'file'" :id="item.id" v-model="fileNames[item.id]" :required="item.required" :readonly="item.readonly" :disabled="item.disabled" :placeholder="$t(item.placeholder || item.label)"
            :accept="item.acceptFileType" :errored="item.errored" :error-message="item.error" @click="uploadFile(item)">
            <template #suffix>
              <CGButton :ref="'fileUploadBtn'+item.id" @click="uploadFile(item)">
                {{ item.noTranslate? item.label : $t(item.label) }}
              </CGButton>
            </template>
          </CGInput>
          <FileUploadModal v-if="item.type == 'file'" :ref="'fileInput'+item.id" :trigger="fileUploadRef" :file-label="'fileInput'+item.id" :max-size="item.maxSize" :allow-multiple="item.multiple" :accepted-formats="item.acceptedFormats" @submit="fileChanged($event, item)" />

        </FormGroup>
      </div>
    </div>

    <template #footer>
      <div class='flex flex-grow items-center justify-between w-full'>
        <div class='w-2/3 text-white'>
          <!-- <ProgressBar v-if='uploadProgress !== -1' :modelValue='uploadProgress' mode='determinate' :show-value='true' color='#1F1584'></ProgressBar> -->
        </div>
        <div class='flex'>
          <CGButton variant="outline" class='mr-3' @click="fileNames={};handleModalClose(modalState=false)">{{ $t('General.Cancel') }}</CGButton>
          <CGButton :disabled="hasError" @click="submitForm()">{{ $t('General.Save') }}</CGButton>
        </div>
      </div>
    </template>

  </Modal>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  emits: ['update'],

  props: {
    data: {
      type: Object,
      default: () => {
        return {};
      }
    },
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    multipleColumns: {
      type: Boolean,
      default: true,
    },
    small: {
      type: Boolean,
      default: false
    },
    fullHeight: {
      type: Boolean,
      default: true
    },
    footerVisible: {
      type: Boolean,
      default: true
    },
    withCloseButton: {
      type: Boolean,
      default: true
    },
    withExpandButton: {
      type: Boolean,
      default: false
    },
    uploadProgress: {
      type: Number,
      default: -1
    },
    trigger: {
      default: null,
    },
  },

  data() {
    return {
      dataItem: {},
      fileNames: {},
      modalState: false,
      fileUploadRef: null,
    };
  },

  computed: {
    hasError() {
      const isEmpty = (value) => {
        if (Array.isArray(value)) return value.length === 0;
        return value === null || value === undefined || value === '';
      };
      
      let errorList = this.fileNames && this.data && this.data.fields && this.data.fields.filter((el) => {
        const value = this.dataItem[el.id];
        const hasRequiredError = el.required && isEmpty(value);
        return el.display && (el.errored || hasRequiredError); // only check the displayed fields
      });
      return errorList && errorList.length > 0;
    },
    visibleFields() {
      return this.data.fields.filter(f => (!('display' in f) || f.display))
    }
  },

  watch:{
    dataItem() {
      this.$props.data.item = this.dataItem
    },
  },

  methods: {
    show(value) {
      this.modalState = value;
      this.fileNames = {}
      this.dataItem = Object.assign({}, this.data.item);
    },
    validate(value, field) {
      field.errored = false
      field.error = ''
      if (typeof field.validator == 'function' && value != '') {
        this.formValid = field.validator(value, this.dataItem[field.id]);
        if (!this.formValid) {
          field.errored = true
          field.error = this.$t(field.errorMessage)
        }
      } else if (field.type == 'number' && 'min' in field && 'max' in field && value != '') {
        if (Number(value) < field.min || Number(value) > field.max) {
          field.errored = true
          field.error = this.$t(field.errorMessage)
        }
      }
    },
    propagateEvent(selected, item) {
      if(item.change && typeof item.change == 'function') {
        item.change(selected);
      }
    },
    uploadFile(item){
      this.fileUploadRef = this.$refs['fileUploadBtn'+item.id]
      this.$refs['fileInput'+item.id][0].showUploadModal(true)
    },
    fileChanged(fileRes, item) {
      this.dataItem[item.id] = (item.multiple)? fileRes.fileList : fileRes.fileList[0]
      let fileNames = fileRes.fileList.map(file=>file.name).join(',')
      this.fileNames[item.id] = fileNames
      this.validate(fileNames, item)
    },
    manageTag(item, tags) {
      this.dataItem[item.id] = tags;
      if (this.dataItem[item.id].length == 0) {
        item['clean'](this.dataItem, item.id);
      } else {
        item['manage'](this.dataItem, item.id);
      }
    },
    async submitForm() {
      this.fileNames = {};
      this.$emit('update', this.dataItem);
    },
    handleModalClose(value) {
      // When the modal is closed, reset the error state of all fields
      if (!value) {
        this.data?.fields?.forEach((item) => {
          item.errored = false;
          item.error = '';
        });
      }
    },
  },
});
</script>

<style>
.modalform .form-group label {
  display: inline-block !important;
}
</style>
